// Every time-series read goes through here. The tenant API rate-limits (429): a list page asks a
// series per row, and a device page opened right after got 429 and showed no chart. So: the same
// request within a minute is answered once (in flight or cached), at most 4 run at a time, and a
// 429 is retried with back-off (Retry-After when the API sends it).
import { api } from "@gimasi/giothub-shell";

type Q = Parameters<typeof api.series>[1];
type R = Awaited<ReturnType<typeof api.series>>;

const TTL = 60_000, MAX = 4, TRIES = 4;
const cache = new Map<string, { at: number; p: Promise<R> }>();
let running = 0;
const queue: (() => void)[] = [];

const slot = () => new Promise<void>((ok) => { if (running < MAX) { running++; ok(); } else queue.push(() => { running++; ok(); }); });
const free = () => { running--; queue.shift()?.(); };
const sleep = (ms: number) => new Promise((ok) => setTimeout(ok, ms));
// from / to are "now"-relative: the same minute is the same request
const minute = (iso?: string) => (iso ? Math.floor(Date.parse(iso) / 60_000) : "");

async function fetchSeries(id: string, q: Q): Promise<R> {
  for (let i = 0; ; i++) {
    await slot();
    try {
      return await api.series(id, q);
    } catch (e) {
      const st = (e as { status?: number }).status;
      if (st !== 429 || i >= TRIES - 1) throw e;
      const ra = Number((e as { retry_after?: number }).retry_after);
      await sleep(ra > 0 ? ra * 1000 : 800 * 2 ** i + Math.random() * 400);
    } finally {
      free();
    }
  }
}

export function series(id: string, q: Q): Promise<R> {
  const k = [id, q.key, minute(q.from), minute(q.to), q.resolution || ""].join("|");
  const hit = cache.get(k);
  if (hit && Date.now() - hit.at < TTL) return hit.p;
  const p = fetchSeries(id, q);
  cache.set(k, { at: Date.now(), p });
  p.catch(() => cache.delete(k)); // a failure is not remembered
  return p;
}

/** forget a device's cached series (new data arrived on the live stream) */
export function dropSeries(id: string) {
  for (const k of cache.keys()) if (k.startsWith(id + "|")) cache.delete(k);
}
